import { useState } from 'react';
import { Search, X, ChevronDown, ChevronUp, Filter } from 'lucide-react';
import Input from '@/components/common/Input';
import Select from '@/components/common/Select';
import Button from '@/components/common/Button';
import Badge from '@/components/common/Badge';
import { useAccounts } from '@/hooks/useAccounts';

const CATEGORIES = [
  { value: '', label: 'All Categories' },
  { value: 'work', label: 'Work' },
  { value: 'personal', label: 'Personal' },
  { value: 'promotion', label: 'Promotional' },
  { value: 'newsletter', label: 'Newsletter' },
  { value: 'receipt', label: 'Receipt' },
  { value: 'notification', label: 'Notification' },
  { value: 'spam', label: 'Spam' },
  { value: 'other', label: 'Other' },
];

const PRIORITIES = [
  { value: '', label: 'All Priorities' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

function getFilterSummary(filters, accounts) {
  const parts = [];
  if (filters.search) parts.push(`"${filters.search}"`);
  if (filters.account_id) {
    const acc = accounts?.find(a => a.id === filters.account_id);
    parts.push(acc?.name || 'Account');
  }
  if (filters.category) {
    const cat = CATEGORIES.find(c => c.value === filters.category);
    parts.push(cat?.label || filters.category);
  }
  if (filters.priority) {
    const pri = PRIORITIES.find(p => p.value === filters.priority);
    parts.push(pri?.label || filters.priority);
  }
  if (filters.seen === false) parts.push('Unread');
  if (filters.action_needed) parts.push('Action needed');
  if (filters.starred) parts.push('Starred');
  if (filters.archived) parts.push('Archived');
  return parts;
}

export default function EmailFilters({ filters, onChange, onClear }) {
  const { data: accounts } = useAccounts();
  const [mobileExpanded, setMobileExpanded] = useState(false);

  const handleChange = (key, value) => {
    onChange({ ...filters, [key]: value });
  };

  const hasActiveFilters = filters.search !== '' ||
    filters.account_id !== '' ||
    filters.category !== '' ||
    filters.priority !== '' ||
    filters.seen === false ||
    filters.action_needed === true ||
    filters.starred === true ||
    filters.archived === true;

  const summaryParts = getFilterSummary(filters, accounts);

  return (
    <div className="space-y-4 p-4 border rounded-lg bg-card">
      {/* Mobile collapsed filter bar */}
      <div className="md:hidden">
        <button
          onClick={() => setMobileExpanded(!mobileExpanded)}
          className="flex items-center justify-between w-full text-left"
        >
          <div className="flex items-center gap-2 min-w-0">
            <Filter className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <span className="font-medium text-sm">Filter</span>
            {summaryParts.length > 0 && (
              <div className="flex items-center gap-1 flex-wrap min-w-0">
                {summaryParts.map((part, i) => (
                  <Badge key={i} variant="outline" className="text-xs whitespace-nowrap">
                    {part}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onClear();
                }}
                className="flex items-center gap-1"
              >
                <X className="h-4 w-4" />
                Clear
              </Button>
            )}
            {mobileExpanded ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </button>
      </div>

      {/* Full filter UI - always visible on desktop, toggled on mobile */}
      <div className={`${mobileExpanded ? 'block' : 'hidden'} md:block`}>
        <div className="hidden md:flex items-center justify-between">
          <h3 className="font-medium">Filters</h3>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClear}
              className="flex items-center gap-1"
            >
              <X className="h-4 w-4" />
              Clear
            </Button>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mt-4 md:mt-0">
          {/* Search */}
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search emails..."
                value={filters.search || ''}
                onChange={(e) => handleChange('search', e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Account */}
          <Select
            label="Account"
            value={filters.account_id || ''}
            onChange={(e) => handleChange('account_id', e.target.value)}
          >
            <option value="">All Accounts</option>
            {accounts?.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </Select>

          {/* Category */}
          <Select
            label="Category"
            value={filters.category || ''}
            onChange={(e) => handleChange('category', e.target.value)}
          >
            {CATEGORIES.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </Select>

          {/* Priority */}
          <Select
            label="Priority"
            value={filters.priority || ''}
            onChange={(e) => handleChange('priority', e.target.value)}
          >
            {PRIORITIES.map((pri) => (
              <option key={pri.value} value={pri.value}>
                {pri.label}
              </option>
            ))}
          </Select>
        </div>

        {/* Toggles */}
        <div className="flex items-center gap-4 flex-wrap mt-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.seen === false}
              onChange={(e) => handleChange('seen', e.target.checked ? false : undefined)}
              className="h-4 w-4 rounded border-input"
            />
            <span className="text-sm">Unread only</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.action_needed === true}
              onChange={(e) => handleChange('action_needed', e.target.checked ? true : undefined)}
              className="h-4 w-4 rounded border-input"
            />
            <span className="text-sm">Action needed</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.starred || false}
              onChange={(e) => handleChange('starred', e.target.checked)}
              className="h-4 w-4 rounded border-input"
            />
            <span className="text-sm">Starred only</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.archived || false}
              onChange={(e) => handleChange('archived', e.target.checked)}
              className="h-4 w-4 rounded border-input"
            />
            <span className="text-sm">Archived</span>
          </label>
        </div>
      </div>
    </div>
  );
}
